import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { MenuItem } from '@openng/optimus-ui/api';
import { distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { MenuService } from '~ngx-shared/layout';
import { Aggregate } from '~ngx-shared/models';
import { UserService } from '~shared/services';
import { RoleEnum } from '~shared/utils';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly userService = inject(UserService);
  private readonly menuService = inject(MenuService);
  private readonly apollo = inject(Apollo);

  subscriptions: Subscription[] = [];

  changeMenuItem = new Subject<MenuItem>();

  constructor() {
    this.destroyRef.onDestroy(() => this.subscriptions.forEach(s => s.unsubscribe()));
    this.changeMenuItem.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(item => {
      const menuItem = this.menuService.getItemFromRoute(item.routerLink);
      if (!menuItem) return;

      menuItem.badge = item.badge;
    });
  }

  init(): void {
    this.userService.user$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(user => {
        // Unsubscribe to clear old ones
        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        if (this.authorizationService.can(RoleEnum.MODERATOR) || user) {
          const personId = user?.person_id || 0;

          this.subscriptions.push(
            this.apollo
              .subscribe<{ result?: Aggregate }>({
                query: gql`
                  subscription GetEvenEvent($personId: bigint!) {
                    result: event_event_aggregate(
                      where: {
                        is_announcement: { _eq: false }
                        education_event_invites: { person_id: { _eq: $personId } }
                        has_response: { _eq: true }
                        _not: { event_responses: {} }
                        created_by_person_id: { _neq: $personId }
                      }
                    ) {
                      aggregate {
                        count
                      }
                    }
                  }
                `,
                variables: {
                  personId
                }
              })
              .subscribe(queryResult => {
                this.changeMenuItem.next({
                  routerLink: ['/', 'events'],
                  badge: queryResult.data?.result?.aggregate?.count
                    ? String(queryResult.data?.result?.aggregate?.count)
                    : undefined
                });
              })
          );
        }
      });
  }
}
